/**
 * Console INFRATEX SMPP — trois environnements.
 *
 *   Espace client   production : campagnes, journal, clés, facturation
 *   Bac à sable     intégration : console API, numéros de test, exemples
 *   Administration  plateforme : routage, comptes, expéditeurs, tarifs, registre
 *
 * La liste des environnements ouverts vient du serveur (`/console-api/me`) et
 * n'est jamais déduite côté navigateur. Masquer un onglet n'est pas une
 * protection : chaque route vérifie les droits de son côté.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, jetonStocke, oublierJeton, type Espace, type Identite } from './api/client';
import { Chargement } from './components/Ui';
import { FournirPlateforme } from './lib/plateforme';
import { formaterFcfa, initiales } from './lib/format';
import { Connexion } from './pages/Connexion';
import { Presentation } from './pages/Presentation';
import { Inscription } from './pages/Inscription';
import { TableauDeBord } from './pages/client/TableauDeBord';
import { Campagnes } from './pages/client/Campagnes';
import { DetailMessage, Journal } from './pages/client/Journal';
import { ClesApi, Expediteurs, Facturation } from './pages/client/Autres';
import { Contacts } from './pages/client/Contacts';
import { Modeles } from './pages/client/Modeles';
import { Webhooks } from './pages/client/Webhooks';
import { ConsoleApi, Exemples, NumerosDeTest } from './pages/sandbox/BacASable';
import {
  Comptes,
  Conformite,
  ExpediteursAValider,
  FacturationAdmin,
  RegistreConsentement,
  Supervision,
  Tarifs,
} from './pages/admin/Administration';
import { Utilisateurs } from './pages/admin/Utilisateurs';
import { Equipe } from './pages/client/Equipe';

type Page =
  // Espace client — le métier
  | 'dash'
  | 'campaigns'
  | 'contacts'
  | 'senders'
  | 'templates'
  | 'journal'
  | 'billing'
  | 'team'
  // Espace développeur — l'intégration
  | 'console'
  | 'keys'
  | 'webhooks'
  | 'techlog'
  | 'numbers'
  | 'snippets'
  // Administration
  | 'ops'
  | 'clients'
  | 'users'
  | 'sids'
  | 'pricing'
  | 'invoicing'
  | 'compliance'
  | 'consent'
  // Hors navigation
  | 'message';

interface Onglet {
  page: Page;
  libelle: string;
}

/**
 * Navigation par espace — **qui vous êtes**.
 *
 * Le bac à sable n'y figure plus : c'est un environnement d'exécution, pas un
 * public. Il est devenu un interrupteur, commun à tous les espaces. Les écrans
 * de clés et de webhooks ne sont donc plus dupliqués, et le développeur d'un
 * client réel a enfin une place à lui.
 */
const NAVIGATION: Record<Espace, Onglet[]> = {
  client: [
    { page: 'dash', libelle: 'Tableau de bord' },
    { page: 'campaigns', libelle: 'Campagnes' },
    { page: 'contacts', libelle: 'Contacts et listes' },
    { page: 'templates', libelle: 'Modèles de message' },
    { page: 'senders', libelle: 'Expéditeurs' },
    { page: 'journal', libelle: 'Suivi des envois' },
    { page: 'billing', libelle: 'Crédits et facturation' },
    { page: 'team', libelle: 'Équipe du compte' },
  ],
  developer: [
    { page: 'console', libelle: 'Console API' },
    { page: 'keys', libelle: 'Clés API' },
    { page: 'webhooks', libelle: 'Webhooks' },
    { page: 'techlog', libelle: 'Journal technique' },
    { page: 'numbers', libelle: 'Numéros de test' },
    { page: 'snippets', libelle: 'Exemples de code' },
  ],
  admin: [
    { page: 'ops', libelle: 'Supervision du routage' },
    { page: 'clients', libelle: 'Comptes clients' },
    { page: 'users', libelle: 'Utilisateurs de la plateforme' },
    { page: 'sids', libelle: 'Expéditeurs à valider' },
    { page: 'pricing', libelle: 'Tarifs et marges' },
    { page: 'invoicing', libelle: 'Facturation et recharges' },
    { page: 'consent', libelle: 'Registre de consentement' },
    { page: 'compliance', libelle: 'Conformité et rétention' },
  ],
};

const LIBELLE_ESPACE: Record<Espace, string> = {
  client: 'Espace client',
  developer: 'Espace développeur',
  admin: 'Administration',
};

const TITRES: Record<Page, string> = {
  dash: 'Tableau de bord',
  campaigns: 'Campagnes',
  contacts: 'Contacts et listes',
  senders: 'Expéditeurs',
  templates: 'Modèles de message',
  journal: 'Suivi des envois',
  keys: 'Clés API',
  webhooks: 'Webhooks',
  techlog: 'Journal technique',
  billing: 'Crédits et facturation',
  team: 'Équipe du compte',
  console: 'Console API',
  numbers: 'Numéros de test',
  snippets: 'Exemples de code',
  ops: 'Supervision du routage',
  clients: 'Comptes clients',
  users: 'Utilisateurs de la plateforme',
  sids: 'Expéditeurs à valider',
  pricing: 'Tarifs et marges',
  invoicing: 'Facturation et recharges',
  compliance: 'Conformité et rétention',
  consent: 'Registre de consentement',
  message: 'Détail du message',
};

type EcranVisiteur = 'presentation' | 'connexion' | 'inscription';

/**
 * Toute autre ancre — `#fonctions`, `#questions`… — appartient à la
 * présentation : ce sont ses sections.
 */
function ecranDepuisAdresse(): EcranVisiteur {
  const ancre = window.location.hash.slice(1);
  return ancre === 'connexion' || ancre === 'inscription' ? ancre : 'presentation';
}

export function App() {
  const [identite, setIdentite] = useState<Identite | null>(null);
  const [verification, setVerification] = useState(true);
  const [espace, setEspace] = useState<Espace>('client');
  /**
   * Écran du visiteur, porté par l'adresse : `#connexion` et `#inscription`
   * mènent droit au formulaire. Chaque passage d'un écran à l'autre ajoute une
   * entrée à l'historique, pour que « Précédent » ramène à l'écran d'avant
   * au lieu de quitter la plateforme.
   */
  const [ecranVisiteur, setEcranVisiteur] = useState<EcranVisiteur>(ecranDepuisAdresse);

  useEffect(() => {
    const suivre = () => setEcranVisiteur(ecranDepuisAdresse());
    window.addEventListener('popstate', suivre);
    return () => window.removeEventListener('popstate', suivre);
  }, []);

  function afficherEcran(cible: EcranVisiteur, { remplacer = false } = {}) {
    const adresse =
      cible === 'presentation' ? window.location.pathname + window.location.search : `#${cible}`;
    if (remplacer) window.history.replaceState(null, '', adresse);
    else window.history.pushState(null, '', adresse);
    setEcranVisiteur(cible);
    window.scrollTo(0, 0);
  }
  const [page, setPage] = useState<Page>('dash');
  const [messageOuvert, setMessageOuvert] = useState<string | null>(null);
  const [numeroPropose, setNumeroPropose] = useState<string | null>(null);
  const [solde, setSolde] = useState<number | null>(null);

  const verifierSession = useCallback(async () => {
    if (!jetonStocke()) {
      setIdentite(null);
      setVerification(false);
      return;
    }
    try {
      const moi = await api.moi();
      setIdentite(moi);
      // L'espace d'arrivée vient du rôle : un développeur atterrit sur la
      // console API, pas sur le tableau de bord des campagnes.
      const arrivee = moi.default_space;
      setEspace(arrivee);
      setPage(NAVIGATION[arrivee][0]!.page);
    } catch {
      oublierJeton();
      setIdentite(null);
    } finally {
      setVerification(false);
    }
  }, []);

  useEffect(() => {
    void verifierSession();
  }, [verifierSession]);

  // Le solde de l'en-tête n'a de sens qu'en production : en bac à sable il est
  // fictif, et l'afficher entretiendrait la confusion entre les deux.
  useEffect(() => {
    // Le solde n'a de sens que sur la plateforme de production : sur celle de
    // test rien n'est facturé, et afficher un solde entretiendrait la confusion.
    if (!identite || identite.platform.mode !== 'production') {
      setSolde(null);
      return;
    }
    api
      .apercu()
      .then((a) => setSolde(a.balance_xaf))
      .catch(() => setSolde(null));
  }, [identite, page]);

  function changerEspace(cible: Espace) {
    setEspace(cible);
    setPage(NAVIGATION[cible][0]!.page);
    setMessageOuvert(null);
    window.scrollTo(0, 0);
  }

  function allerA(cible: Page) {
    setPage(cible);
    window.scrollTo(0, 0);
  }

  async function deconnecter() {
    await api.deconnexion().catch(() => undefined);
    oublierJeton();
    setIdentite(null);
    // Qui se déconnecte connaît le produit : on le rend au formulaire de
    // connexion, pas à la page qui le lui présente. L'entrée est remplacée et
    // non ajoutée : « Précédent » ne doit pas rouvrir la session close.
    afficherEcran('connexion', { remplacer: true });
  }

  const ouvrirMessage = (id: string) => {
    setMessageOuvert(id);
    setPage('message');
  };

  if (verification) return <Chargement quoi="Vérification de la session…" />;

  if (!identite) {
    const reprendre = () => {
      setVerification(true);
      void verifierSession();
    };

    if (ecranVisiteur === 'inscription') {
      return (
        <Inscription surInscription={reprendre} surRetour={() => afficherEcran('connexion')} />
      );
    }
    if (ecranVisiteur === 'connexion') {
      return (
        <Connexion surConnexion={reprendre} surInscrire={() => afficherEcran('inscription')} />
      );
    }

    // Un visiteur qui ne nous connaît pas arrive sur la présentation, pas sur un
    // formulaire. Celui qui revient a son lien de connexion dans l'en-tête, et
    // `#connexion` dans l'adresse le mène droit au formulaire — utile pour un
    // signet ou pour une redirection depuis une session expirée.
    return (
      <Presentation
        surConnexion={() => afficherEcran('connexion')}
        surInscrire={() => afficherEcran('inscription')}
      />
    );
  }

  const espacesOuverts = identite.spaces;
  const plateformeDeTest = identite.platform.mode === 'test';

  /**
   * Les écrans d'argent n'existent pas sur la plateforme de test : rien n'y est
   * facturé, et afficher un solde à zéro sous un seuil d'alerte fabriquerait
   * une inquiétude sans objet. C'est exactement la confusion que la séparation
   * des plateformes doit faire disparaître.
   */
  const estVisible = (onglet: Onglet) => {
    if (plateformeDeTest && onglet.page === 'billing') return false;
    // L'équipe se compose depuis le compte, mais tous les rôles n'en ont pas le
    // droit. On lit la liste que le serveur a calculée — jamais une règle
    // réécrite ici, qui finirait par diverger de celle qui décide des refus.
    if (onglet.page === 'team' && !identite.permissions.includes('users:manage')) return false;
    return true;
  };
  // Nulle part on ne choisit son environnement : la plateforme est ce qu'elle
  // est. On le dit clairement plutôt que de laisser deviner.

  return (
    <FournirPlateforme valeur={identite.platform}>
      <div className="appli">
        <aside className="barre-laterale">
          <div className="marque">
            <div className="ligne">
              {/* Marque officielle, le même fichier que l'icône d'onglet et la
                  page de présentation : une seule source pour tout le produit. */}
              <img className="signe" src="/favicon.svg" alt="" width="26" height="26" />
              <div className="nom">
                INFRATEX
                <br />
                SMPP
              </div>
            </div>
            <div className="produit">Console</div>
          </div>

          <div className="groupe-lateral">
            <div className="intitule">Espace</div>
            {(['client', 'developer', 'admin'] as Espace[])
              .filter((e) => espacesOuverts.includes(e))
              .map((e, i) => (
                <button
                  key={e}
                  type="button"
                  className="bouton-env"
                  aria-pressed={espace === e}
                  onClick={() => changerEspace(e)}
                >
                  <span className="num">{i + 1}</span>
                  <span>{LIBELLE_ESPACE[e]}</span>
                </button>
              ))}
          </div>

          <nav>
            {NAVIGATION[espace].filter(estVisible).map((onglet) => (
              <button
                key={onglet.page}
                type="button"
                className="bouton-nav"
                aria-current={page === onglet.page ? 'page' : undefined}
                onClick={() => allerA(onglet.page)}
              >
                {onglet.libelle}
              </button>
            ))}
          </nav>

          <div className="pied-lateral">
            {identite.account.name}
            <br />
            Débit accordé {identite.account.tps_limit} SMS/s
            {identite.account.sandbox_only && (
              <>
                <br />
                <span style={{ color: 'var(--orange)' }}>Compte d’intégration</span>
              </>
            )}
          </div>
        </aside>

        <div className="colonne-principale">
          {plateformeDeTest && (
            <div className="bandeau-plateforme" role="status">
              <strong>Plateforme de test.</strong> Aucun message n’atteint un réseau GSM, rien n’est
              facturé. La production est un service distinct, avec ses propres comptes et ses
              propres clés — INFRATEX l’ouvre après votre intégration.
            </div>
          )}
          <header className="entete">
            <div>
              <div className="fil">{LIBELLE_ESPACE[espace]}</div>
              <h1>{TITRES[page]}</h1>
            </div>
            <div className="droite">
              {solde !== null && (
                <div className="solde">
                  <div className="valeur">{formaterFcfa(solde)}</div>
                  <div className="libelle">Solde du compte</div>
                </div>
              )}
              <button className="lien" onClick={deconnecter} style={{ fontSize: 13 }}>
                Déconnexion
              </button>
              <div
                className="avatar"
                title={`${identite.user.email}${identite.user.is_platform_admin ? ' — administrateur de plateforme' : ''}`}
              >
                {initiales(identite.user.full_name, identite.user.email)}
              </div>
            </div>
          </header>

          <main className="contenu">
            {/* --- Espace client --- */}
            {page === 'dash' && (
              <TableauDeBord
                estAdmin={identite.user.is_platform_admin}
                surVoirMessage={ouvrirMessage}
                surAllerAuJournal={() => allerA('journal')}
              />
            )}
            {page === 'campaigns' && <Campagnes />}
            {page === 'contacts' && <Contacts />}
            {page === 'senders' && <Expediteurs />}
            {page === 'templates' && <Modeles />}
            {page === 'journal' && <Journal surVoirMessage={ouvrirMessage} />}
            {page === 'billing' && <Facturation />}

            {/* --- Espace développeur --- */}
            {page === 'console' && <ConsoleApi numero={numeroPropose} />}
            {page === 'keys' && <ClesApi />}
            {page === 'webhooks' && <Webhooks />}
            {page === 'techlog' && <Journal surVoirMessage={ouvrirMessage} technique />}
            {page === 'numbers' && (
              <NumerosDeTest
                surEssayer={(numero) => {
                  setNumeroPropose(numero);
                  allerA('console');
                }}
              />
            )}
            {page === 'snippets' && <Exemples />}

            {/* --- Administration --- */}
            {page === 'ops' && <Supervision />}
            {page === 'clients' && <Comptes />}
            {page === 'users' && <Utilisateurs />}
            {page === 'team' && <Equipe />}
            {page === 'sids' && <ExpediteursAValider />}
            {page === 'pricing' && <Tarifs />}
            {page === 'invoicing' && <FacturationAdmin />}
            {page === 'consent' && <RegistreConsentement />}
            {page === 'compliance' && <Conformite />}

            {/* --- Hors navigation --- */}
            {page === 'message' && messageOuvert && (
              <DetailMessage
                id={messageOuvert}
                surRetour={() => allerA(espace === 'developer' ? 'techlog' : 'journal')}
              />
            )}
          </main>
        </div>
      </div>
    </FournirPlateforme>
  );
}
